import { createColumnHelper, DeepKeys, DeepValue } from '@tanstack/react-table';
import { TableCellWrapper } from '../../visual-components/table/table-cell-wrapper/table-cell-wrapper';
import { TableHeadingBasic } from '../../visual-components/table/table-headings/table-heading-basic';
import { notNil } from '../../core/helpers/is-nil';
import { TableFooterBasic } from '../../visual-components/table/table-footers/table-footer-basic';
import { isDisplayColumn, MetaValueConfiguration, withMetaValues } from './helpers';
import { ColumnDefinition } from './types';

const extractCellValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return '';
};

type CreateColumnsOptions<TDefinition> = {
  colDef: ColumnDefinition<TDefinition>[];
  metaValueConfig?: MetaValueConfiguration;
  rowIdentifierColumnId: DeepKeys<TDefinition> & string; // this is used to identify the row ID in the table
  lightHeader?: boolean;
};

export const createColumns = <TDefinition,>({
  colDef,
  metaValueConfig,
  rowIdentifierColumnId,
  lightHeader = false,
}: CreateColumnsOptions<TDefinition>) => {
  const colHelper = createColumnHelper<TDefinition>();

  const hasAnyFooters = colDef.some((param) => notNil(param.footerLabel));

  const columns = colDef.map((param, index) => {
    const { columnId, cssWidth, isHidden, headerLabel, mobileLabel = headerLabel, footerLabel, alignment } = param;

    if (isDisplayColumn<TDefinition>(param)) {
      const { renderCell } = param;
      return colHelper.display({
        id: columnId as string,
        header: () => (
          <TableHeadingBasic alignment={alignment} lightHeader={lightHeader}>
            {headerLabel}
          </TableHeadingBasic>
        ),
        cell: (cellCtx) => (
          <TableCellWrapper
            className={cellCtx.className}
            colIndex={index + 1}
            isMobileHeader={cellCtx.column.columnDef.meta?.isMobileHeader}
            mobileLabel={cellCtx.column.columnDef.meta?.mobileLabel}
          >
            {renderCell !== undefined
              ? renderCell({
                  value: cellCtx.getValue(),
                  rowId: cellCtx.row.getValue(rowIdentifierColumnId),
                  meta: cellCtx.column.columnDef.meta,
                })
              : extractCellValue(cellCtx.getValue())}
          </TableCellWrapper>
        ),
        footer: hasAnyFooters
          ? () => <TableFooterBasic alignment={alignment}>{footerLabel}</TableFooterBasic>
          : undefined,
        meta: {
          mobileLabel,
          cssWidth,
          isHidden,
        },
      });
    }

    const { renderCell } = param;

    // Currently it was not possible to type the accessor function correctly
    // There might be some underlying problem with the @tanstack/react-table package related to this
    // Check https://github.com/TanStack/table/issues/4241 for more info
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return colHelper.accessor(columnId as any, {
      id: columnId as string,
      header: () => {
        return (
          <TableHeadingBasic key={columnId} alignment={alignment} lightHeader={lightHeader}>
            {headerLabel}
          </TableHeadingBasic>
        );
      },
      cell: (cellCtx) => (
        <TableCellWrapper
          key={columnId}
          className={cellCtx.className}
          colIndex={index + 1}
          isMobileHeader={cellCtx.column.columnDef.meta?.isMobileHeader}
          mobileLabel={cellCtx.column.columnDef.meta?.mobileLabel}
        >
          {renderCell !== undefined
            ? renderCell({
                value: cellCtx.getValue() as DeepValue<TDefinition, DeepKeys<TDefinition>>,
                rowId: cellCtx.row.getValue(rowIdentifierColumnId),
                meta: cellCtx.column.columnDef.meta,
                row: cellCtx.row,
              })
            : extractCellValue(cellCtx.getValue())}
        </TableCellWrapper>
      ),
      footer: hasAnyFooters
        ? () => <TableFooterBasic alignment={alignment}>{footerLabel}</TableFooterBasic>
        : undefined,
      meta: {
        mobileLabel,
        cssWidth,
        isHidden,
      },
    });
  });

  return withMetaValues(columns, metaValueConfig);
};
